import { DefaultOptionType } from 'antd/lib/select';
import { ReactNode, useMemo } from 'react';

interface Props<T> {
  data?: T[];
  valueKey?: keyof T;
  labelKey?: keyof T | ((data: T) => ReactNode);
  initialValues?: DefaultOptionType[] | (() => DefaultOptionType[]);
  customValue?: (data?: T[]) => DefaultOptionType[] | undefined;
}

export function useSelectOptions<T>({ data, valueKey, labelKey, initialValues, customValue }: Props<T>) {
  /**
   *
   * @param options list
   * @param initialValues from av automation setup (for update)
   * @returns options with initialValues included
   */
  const initializeOptions = (
    options?: DefaultOptionType[],
    initialValues?: DefaultOptionType[],
  ): DefaultOptionType[] | undefined => {
    if (!options) return undefined;

    const data: DefaultOptionType[] = options;

    if (initialValues) {
      initialValues.forEach((initialValue) => {
        const exists = options.find((option) => option.value === initialValue.value);

        if (!exists) data.push(initialValue);
      });
    }

    return data;
  };

  const options: DefaultOptionType[] | [] =
    useMemo(() => {
      const mappedData =
        customValue && customValue(data)
          ? customValue(data)
          : data?.map((item: T) => {
              return {
                value: item[valueKey as keyof T] as any,
                label: typeof labelKey === 'function' ? labelKey(item) : (item[labelKey as keyof T] as any),
              };
            }) || [];

      return initializeOptions(
        mappedData,
        Array.isArray(initialValues) ? initialValues : initialValues ? initialValues() : [],
      );
    }, [customValue, data, initialValues, labelKey, valueKey]) || [];

  return {
    data: options,
  };
}
